import type { AnyDoc, CollectionName, CommandName, CommandResult, Order, User } from "@mango-moons/domain";

export type Change = { collection: CollectionName; doc: AnyDoc };
/** Cloud sign-in of a Super Admin: the password was right, now the 6-digit authenticator code is needed. */
export class SecondStepRequired extends Error { constructor(message: string) { super(message); this.name = "SecondStepRequired"; } }
export type StaffAccountInput = { email: string; displayName: string; role: User["role"]; permissions: User["permissions"]; pin?: string | undefined;
  /** Chosen by the Super Admin; a random one is generated when left out. */
  password?: string | undefined; mustChangePassword?: boolean | undefined;
};

/**
 * Everything the UI needs from a data source. `local` keeps the whole restaurant on this device;
 * `firebase` talks to Firebase Auth, Firestore listeners and the trusted `api` Cloud Function.
 */
export interface Backend {
  readonly mode: "local" | "firebase";
  /** Resolves with the restored session (if any) and whether first-run setup is still required. */
  init(): Promise<{ user: User | null; needsSetup: boolean }>;
  /** `code`: a Super Admin's authenticator code, asked for after SecondStepRequired (cloud mode). */
  signIn(login: string, password: string, code?: string): Promise<User>;
  signOut(): Promise<void>;
  changePassword(current: string, next: string): Promise<User>;
  call<N extends CommandName>(name: N, input: unknown): Promise<CommandResult<N>>;
  /** Streams the live window of every collection the user may read. Returns an unsubscribe function. */
  subscribe(user: User, since: string, onChange: (changes: Change[]) => void): () => void;
  fetchRange<T = AnyDoc>(collection: CollectionName, from: string, to: string): Promise<T[]>;
  findOrder(code: string): Promise<Order | undefined>;
  createStaff(input: StaffAccountInput): Promise<{ uid: string; temporaryPassword: string }>;
  resetStaffPassword(uid: string): Promise<{ temporaryPassword: string }>;
  /** Local mode only. */
  bootstrap?(input: { email: string; displayName: string; password: string; mustChangePassword?: boolean }): Promise<void>;
  exportSnapshot?(): Promise<string>;
  restoreSnapshot?(json: string, password?: string): Promise<{ documents: number }>;
  /** Encrypts every later backup (automatic and exported) with a key derived from this password. */
  setBackupPassword?(password: string): Promise<void>;
  backupProtection?(): Promise<{ setAt: string } | null>;
  /** Local mode: a Super Admin regains access with the one-time recovery code and sets a new password. */
  recoverAccount?(login: string, code: string, newPassword: string): Promise<User>;
  /** Cloud: a locked-out Super Admin signs in with the password and the printed recovery code (approves this device, once). */
  signInWithRecovery?(login: string, password: string, recovery: string): Promise<User>;
}

export function temporaryPassword(): string {
  const alphabet = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  return Array.from(crypto.getRandomValues(new Uint8Array(14)), (byte) => alphabet[byte % alphabet.length]).join("");
}
